import { ChangeEvent } from 'react';
import { CoreBox } from '@youscience/core';
import { FinalReportRequest, YesNo, FinalReportSchema } from '@youscience/commonapp-service-common';

import { FormState } from '@constants/commonApp/types';

import { TabSections } from '@interfaces/recommenderWorkspace';

import RadioButtonGroupWithClearButton from '@components/RadioGroupWithClearButton';

import FileUpload from '../../../../common/FileUpload';
import SectionAccordion from '../../../../../../../components/SectionAccordion';
import { getFilename } from '../../../../common/formUtils';

import { sxStyles } from './Summary.styles';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';

export type SummaryFields = Partial<FinalReportRequest[TabSections.Summary]>;

interface SummaryProps {
  summary?: SummaryFields;
  onFormChange: (formState: FormState<SummaryFields>) => void;
  onExpand: () => void;
  onContinue?: () => void;
  isExtended: boolean;
  isSaved: boolean;
  applicantName?: string;
  showError?: boolean;
}

const validator = FinalReportSchema.shape.transcripts;

export const Summary = ({
  summary = {},
  onFormChange,
  onExpand,
  isExtended,
  isSaved,
  applicantName = '',
  showError = false,
}: SummaryProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<SummaryFields, typeof validator>(
    summary,
    validator,
    isSaved,
    onFormChange,
  );

  const handleFileChange =
    ({ optional = false } = {}) =>
    (field: string) =>
    async (file: string | undefined) => {
      setFormStateWithErrors({ [field]: file || (optional ? null : undefined) });
    };

  const handleRadioChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormStateWithErrors({ [field]: event.target.value });
  };

  const isShowUploadBtn = [
    formState.data.courseChange,
    formState.data.applicantGraduated,
    formState.data.evaluationChange,
  ].some((radioValue) => radioValue === YesNo.Yes);

  return (
    <SectionAccordion
      title="Summary"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !!formState.isFormValid}
      isError={showError || !formState.isFormValid}
    >
      <CoreBox sx={sxStyles.accordionDetails}>
        <RadioButtonGroupWithClearButton
          title="Have there been any changes to the senior year courses sent with the original School Report?"
          name="courseChange"
          value={formState.data.courseChange ?? undefined}
          options={YesNo}
          required
          handleRadioChange={handleRadioChange}
          handleClear={() => setFormStateWithErrors({ courseChange: undefined })}
          error={!!formState.errors.courseChange}
          helperText={formState.errors.courseChange}
        />

        <RadioButtonGroupWithClearButton
          title="Did or will the student graduate anticipated this year? "
          name="applicantGraduated"
          value={formState.data.applicantGraduated ?? undefined}
          options={YesNo}
          required
          handleRadioChange={handleRadioChange}
          handleClear={() => setFormStateWithErrors({ applicantGraduated: undefined })}
          error={!!formState.errors.applicantGraduated}
          helperText={formState.errors.applicantGraduated}
        />

        <RadioButtonGroupWithClearButton
          title="Do you wish to update your original evaluation of this applicant?"
          name="evaluationChange"
          value={formState.data.evaluationChange ?? undefined}
          options={YesNo}
          required
          handleRadioChange={handleRadioChange}
          handleClear={() => setFormStateWithErrors({ evaluationChange: undefined })}
          error={!!formState.errors.evaluationChange}
          helperText={formState.errors.evaluationChange}
        />

        {isShowUploadBtn ? (
          <FileUpload
            title="Since you answered yes to one of the preceding questions, please attach the explanation."
            name="changeExplanation"
            initialFile={formState.data.changeExplanation}
            previewTitle="Change Explanation"
            filename={formState.data.changeExplanation ? getFilename(applicantName, 'changeExplanation') : undefined}
            onFileUpload={handleFileChange({ optional: true })}
            onFileDelete={handleFileChange({ optional: true })}
            error={formState.errors.changeExplanation}
          />
        ) : null}
      </CoreBox>
    </SectionAccordion>
  );
};
